import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Row, Col, Form, Card } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { FaPlus, FaTrashAlt, FaSave, FaArrowLeft } from "react-icons/fa";

export default function DailyExpenseEdit() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    const [formData, setFormData] = useState({
        date: '',
        comment: '',
        details: [],
        payments: []
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = () => {
        const token = localStorage.getItem("authToken");
        setLoading(true);

        Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, {
                headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${process.env.REACT_APP_API_URL}/daily-expenses-edit/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
        ])
        .then(([paymentRes, editRes]) => {
            let paymentMethodsData = [];
            if (paymentRes.data) {
                if (Array.isArray(paymentRes.data)) {
                    paymentMethodsData = paymentRes.data;
                } else if (paymentRes.data.paymentMethods && Array.isArray(paymentRes.data.paymentMethods)) {
                    paymentMethodsData = paymentRes.data.paymentMethods;
                } else if (paymentRes.data.data && Array.isArray(paymentRes.data.data)) {
                    paymentMethodsData = paymentRes.data.data;
                }
            }
            setPaymentMethods(paymentMethodsData);
            
            if (editRes.data.dailyExepenseCategory) {
                setExpenseCategories(editRes.data.dailyExepenseCategory);
            }
            
            const expenseData = editRes.data.dailyExpense;
            if (expenseData) {
                let formattedDate = '';
                if (expenseData.date) {
                    const date = new Date(expenseData.date);
                    formattedDate = date.toISOString().split('T')[0];
                }
                
                setFormData({
                    date: formattedDate,
                    comment: expenseData.comment || '',
                    details: expenseData.details?.map((detail, index) => ({
                        id: detail.id || Date.now() + index,
                        category_id: detail.expense_category_id || '',
                        price: detail.amount || '',
                        original_id: detail.id
                    })) || [],
                    payments: expenseData.payments?.map((payment, index) => ({
                        id: payment.id || Date.now() + index,
                        payment_method_id: payment.payment_method_id || '',
                        amount: payment.amount || '',
                        original_id: payment.id
                    })) || []
                });
            }
        })
        .catch(err => {
            console.error('Error fetching data:', err);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('Failed to load expense data'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        })
        .finally(() => setLoading(false));
    };

    const handleMainFormChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        if (errors[e.target.name]) {
            setErrors(prev => ({ ...prev, [e.target.name]: '' }));
        }
    };

    const handleAddDetailRow = () => {
        setFormData(prev => ({
            ...prev,
            details: [...prev.details, { 
                id: Date.now(), 
                category_id: '', 
                price: '' 
            }]
        }));
    };

    const handleRemoveDetailRow = (id) => {
        if (formData.details.length === 1) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('At least one expense detail is required'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }
        setFormData(prev => ({
            ...prev,
            details: prev.details.filter(d => d.id !== id)
        }));
    };

    const handleDetailChange = (id, field, value) => {
        setFormData(prev => ({
            ...prev,
            details: prev.details.map(d =>
                d.id === id ? { ...d, [field]: value } : d
            )
        }));
        if (errors[`details.${field}`]) {
            setErrors(prev => ({ ...prev, [`details.${field}`]: '' }));
        }
    };

    const handleAddPaymentRow = () => {
        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, { 
                id: Date.now(), 
                payment_method_id: '', 
                amount: '' 
            }]
        }));
    };

    const handleRemovePaymentRow = (id) => {
        if (formData.payments.length === 1) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('At least one payment is required'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.filter(p => p.id !== id)
        }));
    };

    const handlePaymentChange = (id, field, value) => {
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.map(p =>
                p.id === id ? { ...p, [field]: value } : p
            )
        }));
        if (errors[`payments.${field}`]) {
            setErrors(prev => ({ ...prev, [`payments.${field}`]: '' }));
        }
    };

    const calculateTotalAmount = () =>
        formData.details.reduce((sum, d) => sum + (parseFloat(d.price) || 0), 0);

    const calculateTotalPayment = () =>
        formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    const getRemainingAmount = () => {
        return calculateTotalAmount() - calculateTotalPayment();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validDetails = formData.details.filter(detail => detail.category_id && detail.price);
        if (validDetails.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please add at least one expense detail'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        const validPayments = formData.payments.filter(payment => payment.payment_method_id && payment.amount);
        if (validPayments.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please add at least one payment'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            return;
        }

        setSubmitting(true);
        
        const payload = {
            date: formData.date,
            comment: formData.comment,
            details: validDetails.map(detail => ({
                category_id: detail.category_id,
                price: detail.price
            })),
            payments: validPayments.map(payment => ({
                payment_method_id: payment.payment_method_id,
                amount: payment.amount
            }))
        };

        const token = localStorage.getItem("authToken");

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/daily-expenses-update/${id}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.dailyExpense) {
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Daily expense updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
                navigate('/daily-expenses');
            }
        } catch (error) {
            console.error('Error updating daily expense:', error);
            if (error.response?.status === 422) {
                setErrors(error.response.data.errors || {});
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response?.data?.message || t('Failed to update daily expense'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t('loading')}</span>
                    </div>
                    <p className="mt-3">{t('loading')}...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-3">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">{t('daily expenses')}</h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/daily-expenses" className="mc-breadcrumb-link">{t('daily expenses')}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t('edit')}</li>
                                </ul>
                            </div>
                            <ButtonComponent
                                type="button"
                                className="btn btn-secondary d-flex align-items-center"
                                onClick={() => navigate('/daily-expenses')}
                            >
                                <FaArrowLeft className="me-1" /> {t('back to list')}
                            </ButtonComponent>
                        </div>
                    </div>
                </Col>

                {/* LEFT SIDE */}
                <Col xl={8}>
                    <div className="expense-left">

                        {/* BASIC INFORMATION */}
                        <Card className="expense-card dashed mb-3">
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="fw-bold small">
                                                {t('date')} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="date"
                                                value={formData.date}
                                                onChange={handleMainFormChange}
                                                className="custom-input"
                                                isInvalid={!!errors.date}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.date?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="fw-bold small">
                                                {t('comment')}
                                            </Form.Label>
                                            <Form.Control
                                                name="comment"
                                                placeholder={t('Main Comment')}
                                                value={formData.comment}
                                                onChange={handleMainFormChange}
                                                className="custom-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* EXPENSE DETAILS */}
                        <Card className="expense-card dashed">
                            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">{t('expense details')}</h6>
                                <ButtonComponent
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={handleAddDetailRow}
                                >
                                    <FaPlus className="me-1" /> {t('add detail')}
                                </ButtonComponent>
                            </Card.Header>
                            <Card.Body>
                                {formData.details.length === 0 ? (
                                    <div className="text-center text-muted py-3">
                                        <p className="mb-0">{t('No expense details added')}</p>
                                    </div>
                                ) : (
                                    formData.details.map((detail, index) => (
                                        <div key={detail.id} className="expense-row">
                                            <Row className="align-items-center">
                                                <Col md={5}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-bold small">
                                                            {t('category')} <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Select
                                                            value={detail.category_id}
                                                            onChange={(e) =>
                                                                handleDetailChange(detail.id, 'category_id', e.target.value)
                                                            }
                                                            className="custom-input"
                                                            isInvalid={!!errors[`details.${index}.category_id`]}
                                                        >
                                                            <option value="">{t('Select Category')}</option>
                                                            {expenseCategories.map(c => (
                                                                <option key={c.id} value={c.id}>{c.name}</option>
                                                            ))}
                                                        </Form.Select>
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors[`details.${index}.category_id`]?.[0]}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>

                                                <Col md={5}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-bold small">
                                                            {t('amount')} <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            placeholder={t('Amount')}
                                                            value={detail.price}
                                                            onChange={(e) =>
                                                                handleDetailChange(detail.id, 'price', e.target.value)
                                                            }
                                                            className="custom-input"
                                                            isInvalid={!!errors[`details.${index}.price`]}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors[`details.${index}.price`]?.[0]}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>
                                                </Col>

                                                <Col md={2} className="text-end">
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleRemoveDetailRow(detail.id)}
                                                    >
                                                        <FaTrashAlt />
                                                    </ButtonComponent>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))
                                )}
                            </Card.Body>
                        </Card>

                    </div>
                </Col>

                {/* RIGHT SUMMARY - PAYMENTS */}
                <Col xl={4}>
                    <div className="expense-summary-card">
                        <h5>{t('summary & payments')}</h5>

                        <div className="summary-item">
                            <span>{t('total expense')}:</span>
                            <strong className="text-danger">{calculateTotalAmount().toFixed(2)}</strong>
                        </div>

                        <hr />

                        {formData.payments.map((payment, index) => (
                            <div key={payment.id} className="payment-row">
                                <Form.Select
                                    value={payment.payment_method_id}
                                    onChange={(e) =>
                                        handlePaymentChange(payment.id, 'payment_method_id', e.target.value)
                                    }
                                    isInvalid={!!errors[`payments.${index}.payment_method_id`]}
                                >
                                    <option value="">{t('Select Payment Method')}</option>
                                    {paymentMethods.map(m => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                </Form.Select>

                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder={t('Amount')}
                                    value={payment.amount}
                                    onChange={(e) =>
                                        handlePaymentChange(payment.id, 'amount', e.target.value)
                                    }
                                    isInvalid={!!errors[`payments.${index}.amount`]}
                                />

                                <ButtonComponent
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleRemovePaymentRow(payment.id)}
                                >
                                    <FaTrashAlt size={14} />
                                </ButtonComponent>
                            </div>
                        ))}

                        <ButtonComponent
                            type="button"
                            className="btn-add mt-2"
                            onClick={handleAddPaymentRow}
                        >
                            <FaPlus className="me-1" /> {t('add payment')}
                        </ButtonComponent>

                        <hr className="mt-3" />

                        <div className="summary-item">
                            <span>{t('total paid')}:</span>
                            <strong className="text-success">{calculateTotalPayment().toFixed(2)}</strong>
                        </div>

                        <div className="summary-item">
                            <span>{t('remaining')}:</span>
                            <strong className={getRemainingAmount() === 0 ? "text-success" : "text-warning"}>
                                {getRemainingAmount().toFixed(2)}
                            </strong>
                        </div>
                    </div>
                </Col>

                {/* SUBMIT BUTTON */}
                <Col xl={12} className="mt-4 text-end">
                    <ButtonComponent 
                        className="btn btn-primary btn-lg" 
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                {t('updating')}
                            </>
                        ) : (
                            <>
                                <FaSave className="me-1" /> {t('update expense')}
                            </>
                        )}
                    </ButtonComponent>
                </Col>

            </Row>
        </PageLayout>
    );
}